import { useCallback, useMemo } from 'react';
import useFetchActionbarAuth from 'src/hooks/useFetchActionbarAuth';
import { useKSK1080_R01Store } from './Store_KSK1080_R01';
import { TabType } from './Store_KSK1080_R01';
import { getItemStockColumns, getWarehouseStockColumns, getLogicCenterStockColumns, getTotalStockColumns, getLotStockColumns } from './components/ColumnDefinitions';
import { showSuccess, ShowSuccessFetch, showWarning } from 'src/utils/toast/toastUtil';
import { downloadSettingExcel } from 'src/utils/blob/blobUtil';
import { exportKSK1080_R01Excel } from './Api_KSK1080_R01';
import { generateRowId } from './utils/rowIdUtils';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
// 훅으로 전환: 컨테이너에서 정상적인 훅 호출로 핸들러를 수급
interface WMS2010ContentProps {
    setMessage: (message: string) => void;
}

export const Content_KSK1080_R01 = (props: WMS2010ContentProps) => {
    const { setMessage } = props;
    
    // 권한 체크
    useFetchActionbarAuth('KSK1080_R01');

    const { 
        KSK1080_R01Filter, 
        activeTab, 
        tabData, 
        setActiveTab, 
        setTabData, 
        setTabSelectedId, 
        setTabLoadingState
    } = useKSK1080_R01Store();

	const { unitList } = useCommonCodeStore();

    // 활성 탭에 따른 데이터/컬럼 매핑
    const { columns, data } = useMemo(() => {
        switch (activeTab) {
            case 'item':
                return { columns: getItemStockColumns(unitList), data: tabData.item };
            case 'warehouse':
                return { columns: getWarehouseStockColumns(unitList), data: tabData.warehouse };
            case 'logiccenter':
                return { columns: getLogicCenterStockColumns(unitList), data: tabData.logiccenter };
            case 'lot':
                return { columns: getLotStockColumns(unitList), data: tabData.lot };
            case 'total':
            default:
                return { columns: getTotalStockColumns(unitList), data: tabData.total };
        }
    }, [activeTab, tabData.total, tabData.item, tabData.warehouse, tabData.logiccenter, tabData.lot, unitList]);

    // 뷰 타입 변환 함수
    const getViewType = useCallback((tab: TabType): string => {
        switch (tab) {
            case 'total': return 'ALL';
            case 'item': return 'ITEM';
            case 'warehouse': return 'WAREHOUSE';
            case 'logiccenter': return 'CENTER';
            case 'lot': return 'LOT';
            default: return 'ALL';
        }
    }, []);

    // 탭 이름 변환 함수 (파일명용)
    const getTabDisplayName = useCallback((tab: TabType): string => {
        switch (tab) {
            case 'total': return '전체재고';
            case 'item': return '품목별재고';
            case 'warehouse': return '창고별재고';
            case 'logiccenter': return '물류센터별재고';
            case 'lot': return 'LOT별재고';
            default: return '전체재고';
        }
    }, []);


    // 탭 변경 시 상태 초기화 
    const changeTabAndFetch = useCallback((tab: TabType, options: { clearData?: boolean; clearSelection?: boolean } = {}) => {
        const { clearData = true, clearSelection = true } = options;
        
        // 활성 탭 변경
        setActiveTab(tab);
        
        // 데이터 및 선택 상태 초기화
        if (clearData) {
            setTabData(tab, []);
        }
        if (clearSelection) {
            setTabSelectedId(tab, undefined);
        }
    }, [setActiveTab, setTabData, setTabSelectedId]);

    // 전체 탭 병렬 조회 
    const fetchData = useCallback(async () => {
        const tabs: TabType[] = ['total', 'item', 'warehouse', 'logiccenter', 'lot'];

        const { getKSK1080_R01Data } = await import('./Api_KSK1080_R01');

        // 각 탭별 응답 건수를 저장할 배열
        const tabCounts: { tab: TabType; count: number }[] = [];

        await Promise.all(tabs.map(async (tab) => {
            try {
                setTabLoadingState(tab, true);

                const viewType = getViewType(tab);                
                const res = await getKSK1080_R01Data(viewType, KSK1080_R01Filter);

                // 강화된 데이터 검증 및 처리
                if (res?.data?.data && Array.isArray(res.data.data)) {
                    
                    
                    // 데이터 유효성 검사
                    const validData = res.data.data.filter((item: any, index: number) => {
                        // 기본적인 데이터 구조 검증
                        const isValid = item && typeof item === 'object';
                        if (!isValid) {
                            console.warn(`${tab} 탭 ${index}번째 데이터가 유효하지 않음:`, item);
                        }
                        return isValid;
                    });
                    
                    // 중복 제거 (행 ID 기준) - 개선된 로직
                    const uniqueData = validData.filter((item: any, index: number, array: any[]) => {
                        const rowId = generateRowId(item, tab);
                        
                        const isFirst = array.findIndex(i => {
                            const compareId = generateRowId(i, tab);
                            return compareId === rowId;
                        }) === index;
                        
                        if (!isFirst) {
                            console.warn(`${tab} 탭 중복 데이터 발견 (${index}번째):`, { 
                                rowId, 
                                item: {
                                    itemCd: item.itemCd,
                                    brandCd: item.brandCd,
                                    logicCenter: item.logicCenter,
                                    warehouseCd: item.warehouseCd
                                }
                            });
                        }
                        
                        return isFirst;
                    });
                    
                    if (uniqueData.length > 0) {
                        setTabData(tab, uniqueData);
                        tabCounts.push({ tab, count: uniqueData.length });
                        // console.log(`${tab} 탭 데이터 처리 완료:`, { 
                        //     original: res.data.data.length,
                        //     valid: validData.length,
                        //     unique: uniqueData.length,
                        //     filtered: res.data.data.length - validData.length,
                        //     duplicates: validData.length - uniqueData.length
                        // });
                    } else {
                        console.warn(`${tab} 탭: 처리 후 유효한 데이터가 없습니다.`);
                        setTabData(tab, []);
                        tabCounts.push({ tab, count: 0 });
                    }
                } else {
                    console.warn(`${tab} 탭: 응답 데이터가 없거나 잘못된 형식입니다.`, res);
                    setTabData(tab, []);
                    tabCounts.push({ tab, count: 0 });
                }
            } catch (e) {
                console.error(`${tab} 탭 데이터 조회 실패:`, e);
                setTabData(tab, []);
                tabCounts.push({ tab, count: 0 });
            } finally {
                setTabLoadingState(tab, false);
            }
        }));
        
        // 각 탭별 건수를 상세하게 표시
        const totalCount = tabCounts.reduce((sum, item) => sum + item.count, 0);
        
        if (totalCount > 0) {
            const tabDetails = tabCounts.map(item => 
                `${getTabDisplayName(item.tab)}: ${item.count}건`
            ).join(', ');
            
			ShowSuccessFetch(setMessage, `전체 재고 조회가 완료되었습니다. (총 ${totalCount}건) `);
			setTimeout(() => setMessage(""), 5000);
        } else {
            showWarning('데이터가 없습니다.');
        }
        
    }, [getViewType, setTabData, setTabLoadingState, KSK1080_R01Filter, getTabDisplayName, setMessage]);

    // 개별 탭 조회 함수
    const fetchSingleTabData = useCallback(async (tab: TabType) => {
        try {
            setTabLoadingState(tab, true);

            const { getKSK1080_R01Data } = await import('./Api_KSK1080_R01');
            const viewType = getViewType(tab);
            const res: any = await getKSK1080_R01Data(viewType, KSK1080_R01Filter);

            // 강화된 데이터 검증 및 처리
            if (res?.data?.data && Array.isArray(res.data.data)) {
                console.log(`${tab} 탭 개별 조회 원본 API 응답:`, {
                    total: res.data.data.length,
                    sample: res.data.data.slice(0, 3),
                    viewType: getViewType(tab)
                });
                
                // 데이터 유효성 검사
                const validData = res.data.data.filter((item: any, index: number) => {
                    const isValid = item && typeof item === 'object';
                    if (!isValid) {
                        console.warn(`${tab} 탭 ${index}번째 데이터가 유효하지 않음:`, item);
                    }
                    return isValid;
                });
                
                // 중복 제거 (행 ID 기준) - 개선된 로직
                const uniqueData = validData.filter((item: any, index: number, array: any[]) => {
                    const rowId = generateRowId(item, tab);

                    const isFirst = array.findIndex(i => {
                        const compareId = generateRowId(i, tab);
                        return compareId === rowId;
                    }) === index;
                    
                    if (!isFirst) {
                        console.warn(`${tab} 탭 중복 데이터 발견 (${index}번째):`, { 
                            rowId, 
                            item: {
                                itemCd: item.itemCd,
                                brandCd: item.brandCd,
                                logicCenter: item.logicCenter,
                                warehouseCd: item.warehouseCd
                            }
                        });
                    }
                    
                    return isFirst;
                });
                
                if (uniqueData.length > 0) {
                    setTabData(tab, uniqueData);
                    // console.log(`${tab} 탭 개별 조회 데이터 처리 완료:`, { 
                    //     original: res.data.data.length,
                    //     valid: validData.length,
                    //     unique: uniqueData.length,
                    //     filtered: res.data.data.length - validData.length,
                    //     duplicates: validData.length - uniqueData.length
                    // });
                    
                    // 개별 탭 조회 완료 메시지
                    ShowSuccessFetch(setMessage, `${getTabDisplayName(tab)} 조회가 완료되었습니다. (총 ${uniqueData.length}건)`);
                    setTimeout(() => setMessage(""), 5000);
                } else {
                    console.warn(`${tab} 탭: 처리 후 유효한 데이터가 없습니다.`);
                    setTabData(tab, []);
                    showWarning(`${getTabDisplayName(tab)} 데이터가 없습니다.`);
                }
            } else {
                console.warn(`${tab} 탭: API 응답 데이터가 없거나 잘못된 형식입니다.`, res);
                setTabData(tab, []);
                showWarning(`${getTabDisplayName(tab)} 데이터가 없습니다.`);
            }
        } catch (e) {
            console.error(`${tab} 탭 데이터 조회 실패:`, e);
            setTabData(tab, []);
            showWarning(`${getTabDisplayName(tab)} 조회 중 오류가 발생했습니다.`);
        } finally {
            setTabLoadingState(tab, false);
        }
    }, [getViewType, setTabData, setTabLoadingState, KSK1080_R01Filter, getTabDisplayName, setMessage]);

    // 탭 변경 시 데이터 조회 처리 - 항상 조회
    const handleTabChange = useCallback(async (tab: TabType) => {
        try {
            // 1. 탭 변경 및 상태 초기화
            changeTabAndFetch(tab, { clearData: true, clearSelection: true });
            
            // 2. 활성 탭 항상 조회 (데이터가 있어도 최신 데이터로 갱신)
            await fetchSingleTabData(tab);
        } catch (error) {
            console.error('탭 변경 중 오류:', error);
        }
    }, [changeTabAndFetch, fetchSingleTabData]);

    // Excel 내보내기 핸들러
    const handleExcelClick = useCallback(async () => {
        try {
            const state = useKSK1080_R01Store.getState();
            const activeTab = state.activeTab;
            const filter = state.KSK1080_R01Filter;

            // 뷰 타입 변환
            const viewType = getViewType(activeTab);
            // API 호출
            const response = await exportKSK1080_R01Excel(viewType, { ...filter });

            // Base64 응답 처리 (다른 모듈과 동일한 방식)
            if (response?.data?.data) {
                // 공통 유틸리티 사용
                downloadSettingExcel({ 
                    responseData: response.data.data, 
                    fileName: `재고조회_${getTabDisplayName(activeTab)}.xlsx`
                });
            } else {

                throw new Error('서버에서 올바른 엑셀 데이터를 받지 못했습니다.');
            }
            
        } catch (error) {
            console.error('Excel 내보내기 실패:', error);
            
            // 사용자에게 에러 알림
            if (error instanceof Error) {
                showWarning(`엑셀 내보내기 실패: ${error.message}`);
            } else {
                showWarning('엑셀 내보내기 중 오류가 발생했습니다.');
            }
        }
    }, [getViewType, getTabDisplayName]);

    return { 
        fetchData, 
        handleExcelClick, 
        handleTabChange,
        columns,
        data
    };
};
